/** Pure expedition generator and decisions. Every displayed risk uses a saved seed and decision index. */
import { Rng } from '../../shared';
import { ROAD_EVENTS, RELICS, type RoadChoice, type RoadEvent } from '../../content/explore/events';
export interface Journey {
  id: string; seed: string; step: number; supply: number; wounds: number; loot: number; careful: boolean;
  mode: 'road' | 'crypt'; events: string[]; status: 'active' | 'complete' | 'retreated' | 'failed';
  history: Array<{ event: string; choice: string; hurt: boolean; loot: number; supplyDelta?: number; woundDelta?: number }>; claimed: boolean; relic: string | null;
  prizeCard?: string;
  rewardLines?: Array<{ key: string; params: Record<string, string | number> }>;
  /** Optional versioned travelling-world state; absence preserves legacy routes. */
  trek?:import('./expedition').Trek;
}
export function newJourney(seed: string, index: number, mode: 'road' | 'crypt', careful: boolean): Journey {
  const key = `${seed}:explore:${index}`;
  const rng = new Rng(key);
  const events = rng.shuffle(ROAD_EVENTS.map(e => e.id));
  return { id: key, seed: key, step: 0, supply: mode === 'crypt' ? 5 : 6, wounds: 0, loot: 0, careful, mode, events,
    status: 'active', history: [], claimed: false, relic: null };
}
export function currentEvent(j: Journey): RoadEvent { return ROAD_EVENTS.find(e => e.id === j.events[j.step % j.events.length]) ?? ROAD_EVENTS[0]; }
export function riskFor(j: Journey, c: RoadChoice): number { return c.risk === 0 ? 0 : Math.min(90, Math.max(5, c.risk + (j.mode === 'crypt' ? 10 : 0) - (j.careful ? 15 : 0))); }
export function journeyLength(j: Journey): number { return j.trek?(j.mode==='crypt'?10:8):j.mode === 'crypt' ? 8 : 6; }
export function chooseRoad(journey: Journey, choiceId: string): Journey {
  if (journey.status !== 'active') return journey;
  const event = currentEvent(journey), c = event.options.find(o => o.id === choiceId);
  if (!c || c.supply > journey.supply) return journey;
  const j = structuredClone(journey);
  const hurt = new Rng(j.seed).stream('decision', j.step, c.id).chance(riskFor(j, c) / 100);
  j.supply = Math.min(9, j.supply - c.supply);
  j.wounds = Math.max(0, j.wounds - c.heal) + (hurt ? c.wound : 0);
  const gained = (hurt ? Math.floor(c.loot / 2) : c.loot) + (!j.careful && !hurt && c.loot > 0 ? 1 : 0);
  j.loot += gained;
  j.history.push({ event: event.id, choice: c.id, hurt, loot: gained, supplyDelta: j.supply - journey.supply, woundDelta: j.wounds - journey.wounds }); j.step++;
  if (j.wounds >= 5) { j.status = 'failed'; j.loot = Math.floor(j.loot / 3); }
  else if (j.step >= journeyLength(j)) { j.status = 'complete'; j.relic = new Rng(j.seed).stream('relic').pick(RELICS); }
  return j;
}
export function retreat(journey: Journey): Journey {
  if (journey.status !== 'active' || journey.step < 1) return journey;
  return { ...structuredClone(journey), status: 'retreated' };
}
